/**
 * Capture in the flow of work (#197): what the extension panel SENDS when a
 * reader confirms, contests, contributes to, or defines a number where they
 * read it. Pure builders — the same records the app makes (#186 corrections,
 * #193 contributions, #151 definitions), bound to the claim's own grounding:
 * metric, perspective, the window as a half-open range, the query's filters.
 * Nothing here computes a verdict; the server signs and stores.
 */

export interface PanelClaim {
  id: string; label: string; rendered?: string | null; value?: number | null; unit?: string | null;
  column?: string | null; window?: { start: string; end: string } | null;
  grounding?: Record<string, any> | null;
  capture?: Record<string, any> | null;
}

export interface WalkContext {
  /** the walk's session, when the panel has walked the number (a confirmation or correction binds to it) */
  sessionId?: string | null;
  /** the walk's verdict, as the panel shows it */
  verdict?: { kind?: string | null; headline?: string | null } | null;
  /** the walk's drivers, best first */
  drivers?: Array<{ kind?: string; id?: string | null; label?: string | null; title?: string | null; dimension?: string | null; segment?: string | null }> | null;
}

export interface ClaimTarget { metric: string; perspective: string; window_start: string; window_end: string; filters: unknown[] | Record<string, unknown> }

function nextDay(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

const TIME_GRAINS = new Set(['day', 'week', 'month', 'quarter', 'year', 'hour']);

function isTimeBucket(g: any, column: string): boolean {
  const named = String((g?.time_columns || {})[column] || '').toLowerCase();
  if (named) return TIME_GRAINS.has(named);
  const idx = column.lastIndexOf('__');
  return idx > 0 && TIME_GRAINS.has(column.slice(idx + 2).toLowerCase());
}

/**
 * A grounded claim as the target a record is bound to — the rule the app's
 * `targetOfClaim` applies: the metric, its perspective, the claim's dates as a
 * half-open window (a date-only end moves to the next day), the query's
 * filters plus a row's dimension values (a null cell as IS NULL). A row that
 * carries a time bucket is a period the server computes: no target.
 */
export function targetOfGroundedClaim(claim: PanelClaim): ClaimTarget | null {
  const g = claim?.grounding;
  const metric = g?.metric_qid;
  const start = g?.window?.start || claim?.window?.start;
  const end = g?.window?.end || claim?.window?.end;
  const perspective = g?.perspective;
  if (!metric || !start || !end || !perspective) return null;
  // the query's filters are a list of leaves, or one root AND/OR group (#297) — a group stays the group it is,
  // and a row's dimension values are ANDed onto it as conditions, never flattened to "no filters"
  const group = !Array.isArray(g?.filters) && g?.filters && typeof g.filters === 'object' && Object.keys(g.filters).length ? g.filters as Record<string, unknown> : null;
  const leaves: unknown[] = Array.isArray(g?.filters) ? [...g.filters] : [];
  const row = g?.row && typeof g.row === 'object' ? g.row : {};
  const rowLeaves: unknown[] = [];
  for (const [dim, value] of Object.entries(row)) {
    if (isTimeBucket(g, dim)) return null;
    rowLeaves.push(value == null ? { dimension: dim, operator: 'IS NULL' } : { dimension: dim, operator: '=', value });
  }
  const filters: unknown[] | Record<string, unknown> = group
    ? (rowLeaves.length ? { type: 'group', logic: 'AND', children: [group, ...rowLeaves.map((l: any) => ({ type: 'condition', ...l }))] } : group)
    : [...leaves, ...rowLeaves];
  return {
    metric, perspective,
    window_start: start.length === 10 ? `${start}T00:00:00Z` : start,
    window_end: end.length === 10 ? `${nextDay(end)}T00:00:00Z` : end,
    filters,
  };
}

function driverOf(d: any): { id: string; kind: string; label: string } | null {
  if (!d) return null;
  const kind = String(d.kind || 'text');
  const id = d.id != null ? String(d.id) : kind === 'segment' && d.dimension ? `${d.dimension}=${d.segment}` : '';
  if (!id) return null;
  const label = d.label || d.title || (kind === 'segment' ? `${d.dimension}=${d.segment}` : id);
  return { id, kind, label: String(label) };
}

/** Confirm: "this verdict is right" — a signed record of the verdict, the window, the perspective, the walk; the driver relationship only when said so. */
export function confirmationAnnotation(claim: PanelClaim, walk: WalkContext, opts: { confirmsDriver?: boolean; note?: string | null } = {}) {
  const target = targetOfGroundedClaim(claim);
  if (!target) return null;
  const top = driverOf((walk.drivers || [])[0]);
  const headline = walk.verdict?.headline || null;
  return {
    kind: 'confirmation',
    ts: target.window_start, ts_end: target.window_end,
    title: `Confirmed: ${headline || claim.label}`.slice(0, 200),
    body: opts.note?.trim() || null,
    source: 'confirmation',
    scope: { metrics: [target.metric], perspective: target.perspective },
    confidence: 1,
    confirmation: {
      verdict_kind: walk.verdict?.kind || null, headline,
      ...(top ? { driver: top.id, driver_kind: top.kind, driver_label: top.label } : {}),
      confirms_driver: !!(opts.confirmsDriver && top),
      session_id: walk.sessionId || null,
      filters: target.filters,
    },
  };
}

export interface ContestInput {
  /** the true driver: one of the walk's, or free text (`kind: text`) — the reason is required either way */
  driver?: { id: string; kind: string; label?: string | null } | null;
  reason: string;
  evidence_url?: string | null;
}

/** Contest: the app's correction (#186) — the true driver named, the reason kept, bound to the walk that was wrong. */
export function contestAnnotation(claim: PanelClaim, walk: WalkContext, input: ContestInput) {
  const target = targetOfGroundedClaim(claim);
  const reason = (input.reason || '').trim();
  if (!target || !reason) return null;
  const driver = input.driver?.id ? { id: String(input.driver.id), kind: String(input.driver.kind || 'text'), label: String(input.driver.label || input.driver.id) }
    : { id: reason.slice(0, 200), kind: 'text', label: reason.slice(0, 80) };
  return {
    kind: 'correction',
    ts: target.window_start, ts_end: target.window_end,
    title: `Corrected: ${driver.label}`.slice(0, 200),
    body: reason,
    source: 'correction',
    scope: { metrics: [target.metric], perspective: target.perspective },
    confidence: 1,
    correction: {
      driver: driver.id, driver_kind: driver.kind, driver_label: driver.label,
      evidence_url: input.evidence_url?.trim() || null,
      original_headline: walk.verdict?.headline || null,
      session_id: walk.sessionId || null,
      filters: target.filters,
    },
  };
}

export interface ContributeInput { note: string; dimension?: string | null; segment?: string | null; driver?: { id: string; kind: string; label?: string | null } | null }

/** Add what you know: the app's contribution (#193) — provisional until the server's dry run says otherwise. */
export function contributionAnnotationFor(claim: PanelClaim, walk: WalkContext, input: ContributeInput) {
  const target = targetOfGroundedClaim(claim);
  const note = (input.note || '').trim();
  if (!target || !note) return null;
  const dimension = input.dimension?.trim() || null;
  const segment = input.segment?.trim() || null;
  if ((dimension && !segment) || (!dimension && segment)) return null;    // a half slice tests nothing
  return {
    kind: 'contribution',
    ts: target.window_start, ts_end: target.window_end,
    title: note.length > 80 ? `${note.slice(0, 77)}…` : note,
    body: note,
    source: 'contribution',
    scope: { metrics: [target.metric], perspective: target.perspective },
    confidence: 1,
    contribution: {
      metric: input.driver?.kind === 'metric' && input.driver.id ? String(input.driver.id) : target.metric,
      ...(dimension && segment ? { dimension, segment } : {}),
      ...(input.driver ? { driver_id: String(input.driver.id), driver_kind: input.driver.kind, driver_label: String(input.driver.label || input.driver.id) } : {}),
      filters: target.filters,
      session_id: walk.sessionId || null,
    },
  };
}

export interface DefineInput { term?: string | null; perspective: string; kind?: string; english?: string | null; sql?: string | null; page?: { url?: string; title?: string } | null }

/** Define it: what `POST /api/claims/define` takes — the claim as read, the term, the meaning in words or SQL. */
export function defineRequest(claim: PanelClaim, input: DefineInput) {
  const english = (input.english || '').trim();
  const sql = (input.sql || '').trim();
  if (!input.perspective || (!english && !sql)) return null;
  return {
    claim: { id: claim.id, label: claim.label, rendered: claim.rendered ?? null, value: claim.value ?? null, unit: claim.unit ?? null, column: claim.column ?? null, window: claim.window ?? null },
    term: (input.term || claim.label || '').trim(),
    perspective: input.perspective,
    kind: input.kind || 'metric',
    ...(sql ? { sql } : { english }),
    provenance: { page_url: input.page?.url || null, page_title: input.page?.title || null },
  };
}

/** What a contribution's dry run says, in a line — the app's `describeValidation`, for the panel. */
export function describeContribution(row: any): string {
  const prov = row?.provenance || {};
  const v = prov.validation || {};
  const tier = prov.tier || v.tier || 'unconfirmed';
  if (v.status === 'validating' || v.status === 'queued') return `saved, provisional — checking against the data… (${tier})`;
  if (v.outcome === 'passed') return `validated against the data — ${tier}${v.reason ? `: ${v.reason}` : ''}`;
  if (v.outcome === 'no_effect') return `saved, but the data does not show it${v.reason ? ` — ${v.reason}` : ''} (${tier})`;
  if (v.outcome === 'untestable') return `saved as knowledge — nothing to test against the data (${tier})`;
  if (v.outcome === 'error') return `saved; the check could not run${v.reason ? ` — ${v.reason}` : ''} (${tier})`;
  return `saved (${tier})`;
}
